import { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { AlertCircle, ShieldAlert } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { COIN_NETWORKS, COIN_OPTIONS, validateAddressFormat } from "../lib/walletValidation";

/**
 * Wallet-in-profile picker. Address + network validation runs SERVER-SIDE on
 * PATCH /players/me — this component adds a frontend guard so an off-rail
 * (coin, network, address) can never be submitted. NEVER collects a private
 * key. Coin↔network combinations are restricted to VG's supported payout
 * rails (see /app/frontend/src/lib/walletValidation.js).
 */

export default function WalletPicker({
  address, network, coin, tag,
  onAddress, onNetwork, onCoin, onTag,
  confirmed, onConfirm,
  idPrefix = "wallet",
}) {
  const { t } = useI18n();
  const nets = COIN_NETWORKS[coin] || COIN_NETWORKS.USDT;
  const singleNetwork = nets.length === 1;
  const isXRP = network === "XRPL";
  const addrError = validateAddressFormat(coin, network, address);

  // Iter 114 — Whenever coin changes AFTER MOUNT, snap network to the coin's
  // first supported rail AND clear the address. Prevents e.g. an LTC address
  // being kept after switching to USDC (would send USDC rebate to LTC chain).
  // Skips the initial pass so a loaded profile isn't wiped on the first
  // render — only user-driven coin changes cause the reset.
  const mountedCoinRef = useRef(coin);
  useEffect(() => {
    if (mountedCoinRef.current === coin) return;  // initial mount OR unchanged
    mountedCoinRef.current = coin;
    if (!nets.includes(network)) onNetwork(nets[0]);
    if (address) onAddress("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coin]);

  // Iter 114 — Explicit network change (only reachable for USDT) also clears
  // the address so an ERC20 → TRC20 flip cannot inherit the wrong address.
  const handleNetworkChange = (n) => {
    onNetwork(n);
    if (address) onAddress("");
  };

  const tagValid = !tag || /^\d+$/.test(String(tag).trim()) && Number(tag) >= 0 && Number(tag) <= 4294967295;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("wallet.coin")}</Label>
          <Select value={coin} onValueChange={onCoin}>
            <SelectTrigger data-testid={`${idPrefix}-coin`} className="rounded-none bg-transparent border-white/20 h-11 mt-2"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
              {COIN_OPTIONS.map((c) => <SelectItem key={c} value={c} data-testid={`coin-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("wallet.network")}</Label>
          {singleNetwork ? (
            <Input readOnly data-testid={`${idPrefix}-network-readonly`} value={nets[0]} className="rounded-none bg-white/[0.03] border-white/20 h-11 mt-2 font-mono text-sm cursor-not-allowed" />
          ) : (
            <Select value={network} onValueChange={handleNetworkChange}>
              <SelectTrigger data-testid={`${idPrefix}-network`} className="rounded-none bg-transparent border-white/20 h-11 mt-2"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-[#151B28] border-white/20 text-[#E8ECF4]">
                {nets.map((n) => <SelectItem key={n} value={n} data-testid={`network-${n}`}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("wallet.address")}</Label>
        <Input
          data-testid={`${idPrefix}-address`}
          value={address}
          onChange={(e) => onAddress(e.target.value)}
          className="rounded-none bg-transparent border-white/20 h-12 mt-2 focus:ring-2 focus:ring-[#2DD4A7] font-mono text-sm tracking-tight"
          placeholder={t("wallet.address.placeholder")}
        />
        {addrError ? (
          <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-[#F26D6D]" data-testid={`${idPrefix}-address-error`}>
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>{addrError}</span>
          </div>
        ) : (
          <p className="text-[11px] text-[#F5B841] mt-2">{t("wallet.help")}</p>
        )}
      </div>
      {isXRP && (
        <div data-testid={`${idPrefix}-xrp-tag-wrap`}>
          <Label className="text-xs uppercase tracking-widest text-[#8B97AE]">{t("wallet.xrpTag")}</Label>
          <Input
            data-testid={`${idPrefix}-xrp-tag`}
            type="text" inputMode="numeric"
            value={tag || ""}
            onChange={(e) => onTag(e.target.value)}
            className="rounded-none bg-transparent border-white/20 h-11 mt-2 font-mono tabular-nums"
            placeholder="0 – 4294967295"
          />
          <p className="text-[11px] text-[#F5B841] mt-2">{t("wallet.xrpTag.help")}</p>
          {!tagValid && (
            <div className="mt-1 flex items-start gap-1.5 text-[11px] text-[#F26D6D]"><AlertCircle className="w-3.5 h-3.5 mt-0.5" />{t("wallet.xrpTag.err")}</div>
          )}
        </div>
      )}
      {address && (
        <div className="border border-[#F5B841]/40 bg-[#F5B841]/[0.06] p-3" data-testid={`${idPrefix}-confirm-block`}>
          <div className="flex items-start gap-2 mb-2 text-[#F5B841]"><ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /><span className="text-[11px] font-mono uppercase tracking-widest">{t("wallet.confirm.eyebrow")}</span></div>
          <div className="mb-3 font-mono text-xs break-all text-[#E8ECF4]" data-testid={`${idPrefix}-confirm-address`}>{address}</div>
          <div className="text-[11px] text-[#8B97AE] mb-3">{t("wallet.network")}: <span className="font-mono text-[#E8ECF4]">{network}</span></div>
          <label className="flex items-start gap-2">
            <Checkbox
              data-testid={`${idPrefix}-confirm-check`}
              checked={!!confirmed}
              onCheckedChange={(v) => onConfirm(!!v)}
              className="mt-0.5 rounded-none border-white/40 data-[state=checked]:bg-[#2DD4A7] data-[state=checked]:text-[#04150F]"
            />
            <span className="text-xs text-[#E8ECF4] leading-relaxed">
              {t("wallet.confirm.leading")} <span className="font-mono text-[#2DD4A7]">{network}</span> {t("wallet.confirm.trailing")}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
